import React, { useContext, useMemo } from 'react';
import { CollapseFilterWrapper } from '../../../components/sidebar-filters/CollapseFilterWrapper';
import { BaseFilterCheckboxSelect } from '../../../components/sidebar-filters/base/BaseFilterCheckboxSelect';
import { BillStatusFilterSelect } from '../../../components/sidebar-filters/filter-select/BillStatusFilterSelect';
import { BillProcessingStatus } from '../../../domain/customer-utility-bill/interface';
import { FILE_PROCESSING_STATUSES, billSourceLabel, formatCustomerName } from '../../../domain/customer-utility-bill-file/interface';
import { useCustomerUtilityBillFileCustomersQuery, useCustomerUtilityBillFileSourcesQuery } from '../../../domain/customer-utility-bill-file/queries';
import { BillFilesSidebarFiltersContext, IBillFilesSidebarFilters, IBillFilesSidebarFiltersContext } from './BillFilesSidebarFiltersContext';

// Keys match the GET /customer-utility-bill-files query params, so the URL filter maps straight onto the request.
export const BillFilesFilterKeys = {
    source: 'sources',
    customer: 'customers',
    status: 'statuses',
} as const;

export type TBillFilesFilterKey = (typeof BillFilesFilterKeys)[keyof typeof BillFilesFilterKeys];

export const BillFilesFilterLabels = {
    [BillFilesFilterKeys.source]: 'Source',
    [BillFilesFilterKeys.customer]: 'Customer Name',
    [BillFilesFilterKeys.status]: 'Status',
} as const;

interface IBillFilesSidebarFiltersProps {
    onFilterChange: (filters: IBillFilesSidebarFilters) => void;
}

export const BillFilesSidebarFilters: React.FC<IBillFilesSidebarFiltersProps> = ({ onFilterChange }) => {
    const { filters, handleFilterChange } = useContext<IBillFilesSidebarFiltersContext>(BillFilesSidebarFiltersContext);

    const { data: sources = [], isLoading: isSourcesLoading } = useCustomerUtilityBillFileSourcesQuery();
    const { data: customers = [], isLoading: isCustomersLoading } = useCustomerUtilityBillFileCustomersQuery();

    const sourceOptions = useMemo(() => sources.map(source => ({ value: source, label: billSourceLabel(source) })), [sources]);
    const customerOptions = useMemo(() => customers.map(customer => ({ value: customer, label: formatCustomerName(customer) })), [customers]);

    const handleBillFilesFilterChange = (key: string, value: unknown) => {
        handleFilterChange(key, value);
        onFilterChange({ ...filters, [key]: value });
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%', overflowY: 'auto' }}>
            {/* Filters */}
            <CollapseFilterWrapper label="Filters">
                <BaseFilterCheckboxSelect
                    label={BillFilesFilterLabels[BillFilesFilterKeys.source]}
                    height={100}
                    multiple
                    resizable
                    searchable={false}
                    selectedCountInLabel
                    showSelectAllOption={false}
                    options={sourceOptions}
                    value={filters[BillFilesFilterKeys.source] || []}
                    onChange={value => handleBillFilesFilterChange(BillFilesFilterKeys.source, value)}
                    loading={isSourcesLoading}
                />
                <BaseFilterCheckboxSelect
                    label={BillFilesFilterLabels[BillFilesFilterKeys.customer]}
                    multiple
                    resizable
                    searchable
                    selectedCountInLabel
                    showSelectAllOption={false}
                    options={customerOptions}
                    value={filters[BillFilesFilterKeys.customer] || []}
                    onChange={value => handleBillFilesFilterChange(BillFilesFilterKeys.customer, value)}
                    loading={isCustomersLoading}
                />
                <BillStatusFilterSelect
                    label={BillFilesFilterLabels[BillFilesFilterKeys.status]}
                    statuses={FILE_PROCESSING_STATUSES}
                    value={(filters[BillFilesFilterKeys.status] || []) as BillProcessingStatus[]}
                    onChange={value => handleBillFilesFilterChange(BillFilesFilterKeys.status, value)}
                />
            </CollapseFilterWrapper>
        </div>
    );
};
